import { useQuery } from '@tanstack/react-query';

import { api } from '../../services/client';
import {
  ICategory,
  IRoomMember,
  ISpace,
  ISpaceDetail,
  ISpaceWithStats,
  ISpaceListStats,
  IPaginated,
} from '../../types';
import { createMutationHook } from './createMutation';
import { buildQueryString } from '../../utils/buildQueryString';

// --- Interfaces ---

export interface ISpaceListParams {
  page: number;
  search: string;
  limit: number;
}

export interface ICreateSpaceData {
  name: string;
  description: string;
  visibility: string;
  joinRule?: string;
  ownerId: string;
}

export interface IUpdateSpaceData {
  name?: string;
  description?: string;
  rules?: string;
  visibility?: string;
  joinRule?: string;
  categoryId?: string;
}

export interface IManageSpaceMemberData {
  spaceId: string;
  userIds: string[];
}

// --- Query Key Factory ---

export const spaceKeys = {
  all: ['spaces'] as const,
  lists: () => [...spaceKeys.all, 'list'] as const,
  list: (params: ISpaceListParams) => [...spaceKeys.lists(), params] as const,
  details: () => [...spaceKeys.all, 'detail'] as const,
  detail: (id: string) => [...spaceKeys.details(), id] as const,
};

// --- Query Hooks ---

export const useSpaceListStats = () =>
  useQuery({
    queryKey: ['space-list-stats'] as const,
    queryFn: () => api.get<ISpaceListStats>('/backoffice/space-list-stats'),
  });

export const useSpaces = (params: ISpaceListParams) =>
  useQuery({
    queryKey: spaceKeys.list(params),
    queryFn: () =>
      api.get<IPaginated<ISpaceWithStats>>(
        `/backoffice/spaces-with-stats?${buildQueryString({
          search: params.search || undefined,
          page: params.page,
          limit: params.limit,
        })}`,
      ),
    placeholderData: (previousData) => previousData,
  });

export const useSpaceDetail = (id: string) =>
  useQuery({
    queryKey: spaceKeys.detail(id),
    queryFn: () => api.get<ISpaceDetail>(`/backoffice/spaces/${id}/detail`),
    enabled: !!id,
  });

// --- Mutation Hooks ---

export const useCreateSpace = createMutationHook<ISpace, ICreateSpaceData>({
  mutationFn: (data) => api.post<ISpace>('/backoffice/spaces', data),
  invalidateKeys: [spaceKeys.lists()],
  successMessage: 'toast.space_created',
  errorMessage: 'toast.space_create_failed',
});

export const useUpdateSpace = createMutationHook<
  ISpace,
  { id: string; data: IUpdateSpaceData }
>({
  mutationFn: ({ id, data }) =>
    api.patch<ISpace>(`/backoffice/spaces/${id}`, data),
  invalidateKeys: (variables) => [
    spaceKeys.lists(),
    spaceKeys.detail(variables.id),
  ],
  successMessage: 'toast.space_updated',
  errorMessage: 'toast.space_update_failed',
});

export const useDeleteSpace = createMutationHook<void, { id: string }>({
  mutationFn: ({ id }) => api.delete<void>(`/backoffice/spaces/${id}`),
  invalidateKeys: [spaceKeys.lists()],
  successMessage: 'toast.space_deleted',
  errorMessage: 'toast.space_delete_failed',
});

export const useManageSpaceMember = createMutationHook<
  void,
  IManageSpaceMemberData
>({
  mutationFn: ({ spaceId, userIds }) =>
    api.patch<void>(`/backoffice/spaces/${spaceId}/admins`, { userIds }),
  invalidateKeys: (variables) => [spaceKeys.detail(variables.spaceId)],
  successMessage: 'toast.space_admins_updated',
  errorMessage: 'toast.space_admins_update_failed',
});

export const useTransferOwnership = createMutationHook<
  void,
  { spaceId: string; ownerId: string }
>({
  mutationFn: ({ spaceId, ownerId }) =>
    api.patch<void>(`/backoffice/spaces/${spaceId}/owner`, { ownerId }),
  invalidateKeys: (variables) => [
    spaceKeys.lists(),
    spaceKeys.detail(variables.spaceId),
  ],
  successMessage: 'toast.ownership_transferred',
  errorMessage: 'toast.ownership_transfer_failed',
});

export const useCreateRoom = createMutationHook<
  void,
  {
    spaceId: string;
    data: {
      name: string;
      description: string;
      isAnnouncement: boolean;
      isPrivate: boolean;
      avatar: string;
      memberIds: string[];
    };
  }
>({
  mutationFn: ({ spaceId, data }) =>
    api.post<void>(`/backoffice/spaces/${spaceId}/rooms`, data),
  invalidateKeys: (variables) => [spaceKeys.detail(variables.spaceId)],
  successMessage: 'toast.room_created',
  errorMessage: 'toast.room_create_failed',
});

// --- Category Hooks ---

export const categoryKeys = {
  all: ['categories'] as const,
};

export const useCategories = () =>
  useQuery({
    queryKey: categoryKeys.all,
    queryFn: () => api.get<ICategory[]>('/admin/categories'),
  });

// --- Room-level Hooks ---

export const roomKeys = {
  all: ['rooms'] as const,
  members: (roomId: string) => [...roomKeys.all, roomId, 'members'] as const,
};

export const useRoomMembers = (roomId: string) =>
  useQuery({
    queryKey: roomKeys.members(roomId),
    queryFn: () =>
      api.get<IRoomMember[]>(`/backoffice/rooms/${roomId}/members`),
    enabled: !!roomId,
  });

export const useMuteRoom = createMutationHook<
  void,
  { roomId: string; muteMode: string }
>({
  mutationFn: ({ roomId, muteMode }) =>
    api.post<void>(`/backoffice/rooms/${roomId}/mute`, { muteMode }),
  invalidateKeys: [spaceKeys.all],
  successMessage: 'toast.room_mute_updated',
  errorMessage: 'toast.room_mute_update_failed',
});

export const useDeleteRoom = createMutationHook<void, string>({
  mutationFn: (roomId) => api.delete<void>(`/backoffice/rooms/${roomId}`),
  invalidateKeys: [spaceKeys.all],
  successMessage: 'toast.room_deleted',
  errorMessage: 'toast.room_delete_failed',
});

export const useAssignRoomAdmin = createMutationHook<
  void,
  { roomId: string; userIds: string[] }
>({
  mutationFn: ({ roomId, userIds }) =>
    api.patch<void>(`/backoffice/rooms/${roomId}/admin`, { userIds }),
  invalidateKeys: [spaceKeys.all, roomKeys.all],
  successMessage: 'toast.room_admins_updated',
  errorMessage: 'toast.room_admins_update_failed',
});

export const useAddSpaceMembers = createMutationHook<
  { success: boolean; addedCount: number },
  { spaceId: string; userIds: string[] }
>({
  mutationFn: ({ spaceId, userIds }) =>
    api.post<{ success: boolean; addedCount: number }>(
      `/backoffice/spaces/${spaceId}/members`,
      { userIds },
    ),
  invalidateKeys: (variables) => [
    spaceKeys.lists(),
    spaceKeys.detail(variables.spaceId),
  ],
  successMessage: 'toast.members_added',
  errorMessage: 'toast.members_add_failed',
});

// --- Room Detail Mutations ---

export const useUpdateRoom = createMutationHook<
  void,
  { roomId: string; data: Record<string, unknown> }
>({
  mutationFn: ({ roomId, data }) =>
    api.patch<void>(`/backoffice/rooms/${roomId}`, data),
  invalidateKeys: [spaceKeys.all, roomKeys.all],
  successMessage: 'toast.room_updated',
  errorMessage: 'toast.room_update_failed',
});

export const useConvertRoomVisibility = createMutationHook<
  void,
  { roomId: string; isPrivate: boolean }
>({
  mutationFn: ({ roomId, isPrivate }) =>
    api.post<void>(`/backoffice/rooms/${roomId}/convert-visibility`, {
      isPrivate,
    }),
  invalidateKeys: [spaceKeys.all, roomKeys.all],
  successMessage: 'common.save_success',
  errorMessage: 'common.save_failed',
});

export const useRemoveRoomMember = createMutationHook<
  void,
  { roomId: string; userId: string }
>({
  mutationFn: ({ roomId, userId }) =>
    api.delete<void>(`/backoffice/rooms/${roomId}/members/${userId}`),
  invalidateKeys: [roomKeys.all],
  successMessage: 'toast.member_removed',
  errorMessage: 'toast.member_remove_failed',
});

export const useAddRoomMembers = createMutationHook<
  void,
  { roomId: string; userIds: string[] }
>({
  mutationFn: ({ roomId, userIds }) =>
    api.post<void>(`/backoffice/rooms/${roomId}/members`, { userIds }),
  invalidateKeys: [roomKeys.all, spaceKeys.all],
  successMessage: 'toast.members_added',
  errorMessage: 'toast.members_add_failed',
});

export const useDeleteMessage = createMutationHook<void, string>({
  mutationFn: (messageId) =>
    api.delete<void>(`/backoffice/messages/${messageId}`),
  invalidateKeys: [roomKeys.all],
  successMessage: 'toast.message_deleted',
  errorMessage: 'toast.message_delete_failed',
});

export const useRemoveSpaceMember = createMutationHook<
  void,
  { spaceId: string; userId: string }
>({
  mutationFn: ({ spaceId, userId }) =>
    api.delete<void>(`/backoffice/spaces/${spaceId}/members/${userId}`),
  invalidateKeys: (variables) => [
    spaceKeys.detail(variables.spaceId),
    spaceKeys.lists(),
  ],
  successMessage: 'toast.member_removed',
  errorMessage: 'toast.member_remove_failed',
});

export const useUpdateSpaceAdmins = createMutationHook<
  void,
  { spaceId: string; userIds: string[] }
>({
  mutationFn: ({ spaceId, userIds }) =>
    api.patch<void>(`/backoffice/spaces/${spaceId}/admins`, { userIds }),
  invalidateKeys: (variables) => [spaceKeys.detail(variables.spaceId)],
  successMessage: 'toast.space_admins_updated',
  errorMessage: 'toast.space_admins_update_failed',
});
